import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareButtonIcon from '@/components/ShareButtonIcon/ShareButtonIcon';
import BeenHereRating, { type VisitStatus } from '@/components/TimelinePlaceCard/BeenHereRating';

interface PlaceModalToolbarProps {
  isLocationSaved: boolean;
  isLocationSaveBusy: boolean;
  onToggleLocationSave: () => Promise<'saved' | 'unsaved' | 'error'>;
  isShareEnabled: boolean;
  onShareClick: () => void;
  visitStatus: VisitStatus;
  onVisitStatusChange: (value: VisitStatus) => void;
  onClose: () => void;
}

/**
 * Figma Place Page "Toolbar": close on the left; Save, I've been here, Share on the right.
 * Mobile ("Place actions"): 36px pills.
 */
const PlaceModalToolbar: React.FC<PlaceModalToolbarProps> = ({
  isLocationSaved,
  isLocationSaveBusy,
  onToggleLocationSave,
  isShareEnabled,
  onShareClick,
  visitStatus,
  onVisitStatusChange,
  onClose,
}) => (
  <div className="sticky top-0 z-20 flex items-center justify-between gap-2 bg-white p-4 max-md:px-5 max-md:pb-2 max-md:pt-[calc(12px+env(safe-area-inset-top,0px))]">
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-white text-[#1F1F1F] transition-opacity hover:opacity-70"
    >
      <CloseIcon sx={{ fontSize: 18 }} />
    </button>

    <div className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        onClick={() => void onToggleLocationSave()}
        disabled={isLocationSaveBusy}
        aria-pressed={isLocationSaved}
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[20px] border border-solid border-black bg-black px-4 max-md:rounded-full max-md:border-[#252528] max-md:bg-[#252528] max-md:px-3 font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-white transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-60 max-md:text-xs max-md:leading-4"
      >
        {isLocationSaved ? (
          <BookmarkIcon sx={{ fontSize: 18 }} aria-hidden />
        ) : (
          <BookmarkBorderIcon sx={{ fontSize: 18 }} aria-hidden />
        )}
        {isLocationSaved ? 'Saved' : 'Save'}
      </button>

      <BeenHereRating value={visitStatus} onChange={onVisitStatusChange} variant="toolbar" />

      {isShareEnabled && (
        <button
          type="button"
          onClick={onShareClick}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[20px] border border-solid border-[#D9D9D9] bg-white px-4 font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-[#252528] transition-colors hover:bg-[#F7F7F7] max-md:gap-1 max-md:rounded-full max-md:border-[#E1E1E7] max-md:px-2.5 max-md:text-xs max-md:leading-4 max-md:text-black"
        >
          <ShareButtonIcon size={18} color="#252528" />
          Share
        </button>
      )}
    </div>
  </div>
);

export default PlaceModalToolbar;
